import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Spinner, Alert, Button, Form, Badge, Table } from 'react-bootstrap';
import { ApiGet } from '../../../../ApiServices/ApiServices';
import { toast } from 'react-toastify';

const Dashboard = () => {
  // Date range state
  const [dateRange, setDateRange] = useState({
    fromDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    toDate: new Date().toISOString().split('T')[0]
  });

  // Dashboard summary state
  const [summaryData, setSummaryData] = useState({
    summary: {
      totalOrders: 0,
      totalRevenue: 0,
      averageOrderValue: 0,
      period: { fromDate: '', toDate: '' }
    },
    orderByStatus: {
      PENDING: 0,
      COMPLETED: 0,
      CANCELLED: 0,
      UNKNOWN: 0,
      WORKING: 0
    },
    revenueTrend: [],
    topRestaurants: [],
    topMenuItems: []
  });

  const [loading, setLoading] = useState({
    summary: false
  });
  const [error, setError] = useState({
    summary: ''
  });
  const [lastRefresh, setLastRefresh] = useState(new Date());

  useEffect(() => {
    fetchDashboardSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDashboardSummary = async () => {
    setLoading(prev => ({ ...prev, summary: true }));
    setError(prev => ({ ...prev, summary: '' }));

    try {
      const result = await ApiGet('/api/admin/dashboard/summary', {
        fromDate: dateRange.fromDate,
        toDate: dateRange.toDate
      });

      if (result.success) {
        setSummaryData(result.success.data.data);
        setLastRefresh(new Date());
      } else {
        setError(prev => ({ ...prev, summary: result.fail }));
      }
    } catch (err) {
      setError(prev => ({ ...prev, summary: 'Failed to fetch dashboard data' }));
    } finally {
      setLoading(prev => ({ ...prev, summary: false }));
    }
  };

  const handleDateChange = (field, value) => {
    setDateRange(prev => ({ ...prev, [field]: value }));
  };

  const handleApplyFilter = () => {
    fetchDashboardSummary();
  };

  const handleRefresh = () => {
    fetchDashboardSummary();
    toast.success('Dashboard refreshed successfully!');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(value || 0);
  };

  const StatCard = ({ icon, title, value, subtitle, iconColor = "#d74440", loading = false, isCurrency = false }) => (
    <Card className="border-0 shadow-sm h-100" style={{ borderRadius: '16px' }}>
      <Card.Body style={{ background: '#ffffff' }}>
        {loading ? (
          <div className="text-center py-1">
            <Spinner animation="border" size="sm" />
            <div className="mt-2 text-muted">Loading...</div>
          </div>
        ) : (
          <div className="d-flex justify-content-between align-items-start">
            <div className="text-dark">
              <div className="fs-6 mb-2 text-muted">{title}</div>
              <div className="fs-2 fw-bold mb-1" style={{ color: '#d74440' }}>
                {isCurrency ? formatCurrency(value) : (value?.toLocaleString() || 0)}
              </div>
              {subtitle && <div className="fs-7 text-muted">{subtitle}</div>}
            </div>
            <div className="p-3 rounded-circle" style={{ backgroundColor: `${iconColor}15` }}>
              <i className={`${icon} fs-4`} style={{ color: iconColor }}></i>
            </div>
          </div>
        )}
      </Card.Body>
    </Card>
  );

  const getStatusBadgeColor = (status) => {
    const colors = {
      'PENDING': 'warning',
      'COMPLETED': 'success',
      'CANCELLED': 'danger',
      'WORKING': 'primary',
      'UNKNOWN': 'secondary'
    };
    return colors[status] || 'secondary';
  };

  const getStatusIcon = (status) => {
    const icons = {
      'PENDING': 'fas fa-clock',
      'COMPLETED': 'fas fa-check-circle',
      'CANCELLED': 'fas fa-times-circle',
      'WORKING': 'fas fa-spinner',
      'UNKNOWN': 'fas fa-question-circle'
    };
    return icons[status] || 'fas fa-circle';
  };

  const totalOrders = Object.values(summaryData.orderByStatus).reduce((a, b) => a + b, 0);

  return (
    <Container fluid className="py-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="mb-0" style={{ color: '#d74440', fontWeight: '700' }}>
          <i className="fas fa-tachometer-alt me-2" style={{ color: '#d74440' }}></i>
          Dashboard
        </h2>
        <div className="d-flex align-items-center gap-3">
          <small className="text-muted">
            Last updated: {lastRefresh.toLocaleTimeString()}
          </small>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={handleRefresh}
            disabled={loading.summary}
            style={{ borderColor: '#d74440', color: '#d74440' }}
          >
            <i className="fas fa-sync-alt me-2"></i>
            Refresh
          </Button>
        </div>
      </div>

      {/* Date Range Filter */}
      <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '16px' }}>
        <Card.Body className="py-3">
          <Row className="align-items-center">
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small text-muted mb-1">From Date</Form.Label>
                <Form.Control
                  type="date"
                  value={dateRange.fromDate}
                  onChange={(e) => handleDateChange('fromDate', e.target.value)}
                  style={{ height: '42px' }}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group>
                <Form.Label className="small text-muted mb-1">To Date</Form.Label>
                <Form.Control
                  type="date"
                  value={dateRange.toDate}
                  onChange={(e) => handleDateChange('toDate', e.target.value)}
                  style={{ height: '42px' }}
                />
              </Form.Group>
            </Col>
            <Col md={2} className="d-flex align-items-end">
              <Button
                style={{ backgroundColor: '#d74440', borderColor: '#d74440', height: '42px', marginTop: '24px' }}
                onClick={handleApplyFilter}
                disabled={loading.summary}
              >
                {loading.summary ? (
                  <>
                    <Spinner animation="border" size="sm" style={{ width: '1rem', height: '1rem' }} className="me-2" />
                    Loading...
                  </>
                ) : (
                  <>
                    <i className="fas fa-filter me-2"></i>
                    Apply Filter
                  </>
                )}
              </Button>
            </Col>
            <Col md={4} className="text-end">
              <div className="text-muted small mt-4">
                <i className="fas fa-calendar-alt me-1"></i>
                Showing data from <strong>{dateRange.fromDate}</strong> to <strong>{dateRange.toDate}</strong>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {error.summary && (
        <Alert variant="danger" className="mb-4">
          <i className="fas fa-exclamation-triangle me-2"></i>
          {error.summary}
        </Alert>
      )}

      {/* Main Stats Row */}
      <Row className="g-4 mb-4">
        <Col lg={4} md={6}>
          <StatCard
            icon="fas fa-shopping-cart"
            title="Total Orders"
            value={summaryData.summary.totalOrders}
            subtitle={`${summaryData.summary.period.fromDate} to ${summaryData.summary.period.toDate}`}
            iconColor="#d74440"
            loading={loading.summary}
          />
        </Col>
        <Col lg={4} md={6}>
          <StatCard
            icon="fas fa-rupee-sign"
            title="Total Revenue"
            value={summaryData.summary.totalRevenue}
            subtitle="Total sales amount"
            iconColor="#059669"
            loading={loading.summary}
            isCurrency={true}
          />
        </Col>
        <Col lg={4} md={6}>
          <StatCard
            icon="fas fa-chart-line"
            title="Average Order Value"
            value={summaryData.summary.averageOrderValue}
            subtitle="Per order average"
            iconColor="#7c3aed"
            loading={loading.summary}
            isCurrency={true}
          />
        </Col>
      </Row>

      {/* Order Status Row */}
      <Row className="g-4 mb-4">
        <Col lg={12}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: '16px' }}>
            <Card.Body>
              <h5 className="mb-4" style={{ color: '#d74440', fontWeight: '600' }}>
                <i className="fas fa-tasks me-2"></i>
                Orders by Status
              </h5>
              {loading.summary ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                </div>
              ) : (
                <Row className="g-3">
                  {Object.entries(summaryData.orderByStatus).map(([status, count]) => (
                    <Col key={status} md={2} sm={4} xs={6}>
                      <Card className={`border-0 bg-${getStatusBadgeColor(status)} bg-opacity-10 h-100`} style={{ borderRadius: '12px' }}>
                        <Card.Body className="text-center py-3">
                          <i className={`${getStatusIcon(status)} fs-3 text-${getStatusBadgeColor(status)} mb-2`}></i>
                          <div className={`fs-2 fw-bold text-${getStatusBadgeColor(status)}`}>{count}</div>
                          <div className="small text-muted text-capitalize">{status.toLowerCase()}</div>
                          {totalOrders > 0 && (
                            <Badge bg={getStatusBadgeColor(status)} className="mt-2">
                              {((count / totalOrders) * 100).toFixed(1)}%
                            </Badge>
                          )}
                        </Card.Body>
                      </Card>
                    </Col>
                  ))}
                </Row>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Revenue Trend and Top Items Row */}
      <Row className="g-4 mb-4">
        {/* Revenue Trend */}
        <Col lg={8}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: '16px' }}>
            <Card.Body>
              <h5 className="mb-4" style={{ color: '#d74440', fontWeight: '600' }}>
                <i className="fas fa-chart-area me-2"></i>
                Revenue Trend
              </h5>
              {loading.summary ? (
                <div className="text-center py-5">
                  <Spinner animation="border" />
                </div>
              ) : summaryData.revenueTrend && summaryData.revenueTrend.length > 0 ? (
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead>
                      <tr>
                        <th>Date</th>
                        <th>Orders</th>
                        <th>Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summaryData.revenueTrend.map((item, index) => (
                        <tr key={index}>
                          <td>{new Date(item.date).toLocaleDateString()}</td>
                          <td><Badge bg="primary">{item.orders || 0}</Badge></td>
                          <td className="fw-bold text-success">{formatCurrency(item.revenue)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-5">
                  <i className="fas fa-chart-bar fs-1 text-muted mb-3 d-block"></i>
                  <p className="text-muted mb-0">No revenue trend data available for this period</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Top Menu Items */}
        <Col lg={4}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: '16px' }}>
            <Card.Body>
              <h5 className="mb-4" style={{ color: '#d74440', fontWeight: '600' }}>
                <i className="fas fa-star me-2"></i>
                Top Menu Items
              </h5>
              {loading.summary ? (
                <div className="text-center py-4">
                  <Spinner animation="border" size="sm" />
                </div>
              ) : summaryData.topMenuItems && summaryData.topMenuItems.length > 0 ? (
                <div className="d-flex flex-column gap-3">
                  {summaryData.topMenuItems.map((item, index) => (
                    <div key={index} className="d-flex align-items-center gap-3">
                      <div className="bg-warning bg-opacity-10 p-2 rounded-circle" style={{ width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span className="fw-bold text-warning">#{index + 1}</span>
                      </div>
                      <div className="flex-grow-1">
                        <div className="fw-semibold">{item.name || item.menuItemName || 'Unknown Item'}</div>
                        <small className="text-muted">
                          {item.orderCount || item.count || 0} orders
                        </small>
                      </div>
                      <Badge bg="success">{formatCurrency(item.revenue || item.totalRevenue || 0)}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <i className="fas fa-utensils fs-1 text-muted mb-3 d-block"></i>
                  <p className="text-muted mb-0">No top items data available</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Top Restaurants Row */}
      <Row className="g-4">
        <Col lg={12}>
          <Card className="border-0 shadow-sm" style={{ borderRadius: '16px' }}>
            <Card.Body>
              <h5 className="mb-4" style={{ color: '#d74440', fontWeight: '600' }}>
                <i className="fas fa-store me-2"></i>
                Top Restaurants
              </h5>
              {loading.summary ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                </div>
              ) : summaryData.topRestaurants && summaryData.topRestaurants.length > 0 ? (
                <div className="table-responsive">
                  <Table hover className="mb-0">
                    <thead>
                      <tr>
                        <th>#</th>
                        <th>Restaurant Name</th>
                        <th>Orders</th>
                        <th>Revenue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summaryData.topRestaurants.map((restaurant, index) => (
                        <tr key={index}>
                          <td>
                            <Badge bg={index < 3 ? 'warning' : 'secondary'}>{index + 1}</Badge>
                          </td>
                          <td className="fw-semibold">{restaurant.name || restaurant.restaurantName || 'Unknown'}</td>
                          <td><Badge bg="primary">{restaurant.orderCount || restaurant.orders || 0}</Badge></td>
                          <td className="fw-bold text-success">{formatCurrency(restaurant.revenue || restaurant.totalRevenue || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
              ) : (
                <div className="text-center py-5">
                  <i className="fas fa-store fs-1 text-muted mb-3 d-block"></i>
                  <p className="text-muted mb-0">No top restaurants data available for this period</p>
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Dashboard;
